import { useForm } from '@tanstack/react-form'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

import {
  TEMPLATE_CATEGORIES,
  TEMPLATE_TRIGGERS,
  TEMPLATE_VARIABLES,
  TRIGGER_VALUE_OPTIONS,
  type MessageTemplate,
  type TemplateWriteBody,
} from '@/types/messaging'

export const templateFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or fewer'),
  category: z.enum(['general', 'customers', 'service']),
  body: z.string().min(1, 'Message body is required'),
  trigger_type: z.enum([
    '',
    'work_order_status_changed',
    'work_order_completed',
    'deal_stage_changed',
    'service_due',
    'registration_temp_tag_expiring',
  ]),
  trigger_value: z.string(),
})

export type TemplateFormValues = z.infer<typeof templateFormSchema>

const fieldSchemas = templateFormSchema.shape

interface TemplateFormProps {
  defaultValues?: Partial<TemplateFormValues>
  onSubmit: (values: TemplateWriteBody) => void
  isPending?: boolean
  submitLabel?: string
}

export function TemplateForm({
  defaultValues,
  onSubmit,
  isPending,
  submitLabel = 'Save',
}: TemplateFormProps) {
  const initialValues: TemplateFormValues = {
    name: '',
    category: 'general',
    body: '',
    trigger_type: '',
    trigger_value: '',
    ...defaultValues,
  }

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: ({ value }) => {
      onSubmit({
        name: value.name,
        category: value.category,
        body: value.body,
        trigger_type: value.trigger_type,
        trigger_value: value.trigger_type ? value.trigger_value : '',
      })
    },
  })

  return (
    <Form form={form}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void form.handleSubmit()
        }}
        className="space-y-4"
      >
        <FormField
          name="name"
          validators={{ onChange: fieldSchemas.name }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Service started" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            name="category"
            validators={{ onChange: fieldSchemas.category }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Category</FormLabel>
                <Select onValueChange={field.onChange} value={field.value as string}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {TEMPLATE_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="trigger_type"
            validators={{ onChange: fieldSchemas.trigger_type }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Auto-send trigger</FormLabel>
                <Select
                  onValueChange={(v) => {
                    field.onChange(v)
                    form.setFieldValue('trigger_value', '')
                  }}
                  value={field.value as string}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Manual only" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="">Manual only</SelectItem>
                    {TEMPLATE_TRIGGERS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <form.Subscribe
          selector={(state) => state.values.trigger_type}
          children={(triggerType) =>
            triggerType ? (
              <FormField
                name="trigger_value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Send when</FormLabel>
                    <Select
                      onValueChange={(v) => field.onChange(v === '__any__' ? '' : v)}
                      value={(field.value as string) || '__any__'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TRIGGER_VALUE_OPTIONS[
                          triggerType as keyof typeof TRIGGER_VALUE_OPTIONS
                        ].map((opt) => (
                          <SelectItem key={opt.value || '__any__'} value={opt.value || '__any__'}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null
          }
        />

        <FormField
          name="body"
          validators={{ onChange: fieldSchemas.body }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Message body</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={`Hi {{first_name}}, we've started {{work_order_title}} on {{boat_name}}.`}
                  className="min-h-24"
                  {...field}
                />
              </FormControl>
              <p className="text-xs text-muted-foreground">
                Variables: {TEMPLATE_VARIABLES.map((v) => `{{${v}}}`).join(' ')}
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isPending} className="w-full">
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </form>
    </Form>
  )
}

export function templateToFormValues(template: MessageTemplate): TemplateFormValues {
  return {
    name: template.name,
    category: template.category,
    body: template.body,
    trigger_type: template.trigger_type ?? '',
    trigger_value: template.trigger_value,
  }
}
